import {
  SubprinciplesConfigurationForm,
  SubprinciplesConfigurationFormValues,
} from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/edit/SubprinciplesConfigurationForm'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { useLocation, useParams } from 'react-router'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  canUpdateSubprincipleStatus,
  fetchPrincipleConfigs,
  mapFetchedPrincipleConfigsToOptions,
  updateSubprincipleMutation,
} from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/edit/SubprinciplesConfigFormUtils'
import { useUserLocale } from 'src/user/UserContext'
import { Option } from 'src/shared/form/control'
import { ROUTES } from 'src/routing/routes'
import { querySubprincipleConfigById } from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/subprinciplesConfigQueries'
import { Query_Root, Subprinciple_Config_Insert_Input } from 'src/@types/graphql'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { getConfigStatusOptions } from 'src/screens/administration/configuration-management/ConfigurationUtils'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

const submitSubprincipleConfigForm = async () => {
  document.getElementById('subprinciples-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const PrinciplesConfigurationSubprinciplesEditPage = () => {
  const { subprincipleId } = useParams()
  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()
  const { state } = useLocation()

  const [initialValues, setInitialValues] = useState<SubprinciplesConfigurationFormValues | null>(null)
  const [parentPrincipleOptions, setParentPrincipleOptions] = useState<Option[]>([])
  const [isSubprincipleAssessedInPrograms, setIsSubprincipleAssessedInPrograms] = useState(false)
  const originRef = useRef<OriginType>('SAVE')

  const initParentPrincipleOptions = useCallback(async () => {
    const { fetchedPrincipleConfigs } = await fetchPrincipleConfigs(urqlClient)()
    const mappedParentPrincipleOptions = mapFetchedPrincipleConfigsToOptions(
      fetchedPrincipleConfigs,
      getMessage,
      locale,
    )

    return {
      parentPrincipleOptions: mappedParentPrincipleOptions,
    }
  }, [getMessage, locale, urqlClient])

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])

  const onBackHandler = useCallback(() => {
    const navigatedFromSortListView =
      state?.navigatedFromSortListView !== undefined &&
      state?.navigatedFromSortListView !== null &&
      state.navigatedFromSortListView === true

    if (navigatedFromSortListView) {
      navigate(ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationPreview.params({}))
    } else {
      navigate(ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationSubprinciples.params({}))
    }
  }, [navigate, state])

  const onSave = useCallback(() => {
    originRef.current = 'SAVE'
    submitSubprincipleConfigForm()
  }, [])

  const onSaveAndBack = useCallback(() => {
    originRef.current = 'SAVE_AND_BACK'
    submitSubprincipleConfigForm()
  }, [])

  const fetchSubprincipleConfigToEdit = useCallback(async () => {
    if (!subprincipleId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{ subprinciple_config: Query_Root['subprinciple_config'] }>(querySubprincipleConfigById, {
        subprincipleId,
      })
      .toPromise()

    if (error || !data?.subprinciple_config?.[0]) {
      throw new Error('Resource not found')
    }

    const fetchedSubprincipleConfig = data.subprinciple_config[0]

    const editingSubprincipleConfig: SubprinciplesConfigurationFormValues = {
      enName: fetchedSubprincipleConfig.names['en'],
      deName: fetchedSubprincipleConfig.names['de'],
      frName: fetchedSubprincipleConfig.names['fr'],
      itName: fetchedSubprincipleConfig.names['it'],
      enHelpInstructions: fetchedSubprincipleConfig.descriptions['en'],
      deHelpInstructions: fetchedSubprincipleConfig.descriptions['de'],
      frHelpInstructions: fetchedSubprincipleConfig.descriptions['fr'],
      itHelpInstructions: fetchedSubprincipleConfig.descriptions['it'],
      status: fetchedSubprincipleConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      parentPrinciple: fetchedSubprincipleConfig.principle_config_id,
      helpAndInstructionsKey: fetchedSubprincipleConfig.help_and_instruction_key,
    }

    const subprincipleTimesAssessed = data?.subprinciple_config[0].kap_program_subprinciples_aggregate.aggregate?.count
    if (subprincipleTimesAssessed == null) {
      throw new Error('Resource not found')
    }

    return { editingSubprincipleConfig, subprincipleTimesAssessed }
  }, [subprincipleId, urqlClient])

  const validateAndHandleSubmit = useCallback(
    async (values: SubprinciplesConfigurationFormValues, setFormDirty: Dispatch<boolean>) => {
      if (subprincipleId == null || values.parentPrinciple == null) {
        notificationService.operationFailed()
        return
      }

      if (!canUpdateSubprincipleStatus(values.parentPrinciple, values.status, parentPrincipleOptions)) {
        notificationService.error(getMessage('notification.edit.subprinciples.status.invalid'))
        return
      }

      const subprincipleSetInput: Subprinciple_Config_Insert_Input = {
        names: {
          de: values.deName ?? '',
          en: values.enName ?? '',
          fr: values.frName ?? '',
          it: values.itName ?? '',
        },
        descriptions: {
          de: values.deHelpInstructions ? HtmlSanitizer.sanitize(values.deHelpInstructions) : '',
          en: values.enHelpInstructions ? HtmlSanitizer.sanitize(values.enHelpInstructions) : '',
          fr: values.frHelpInstructions ? HtmlSanitizer.sanitize(values.frHelpInstructions) : '',
          it: values.itHelpInstructions ? HtmlSanitizer.sanitize(values.itHelpInstructions) : '',
        },
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        ...(!isSubprincipleAssessedInPrograms && { principle_config_id: values.parentPrinciple }),
      }

      const subprincipleIdParsed = parseInt(subprincipleId)
      try {
        await updateSubprincipleMutation(urqlClient)(subprincipleIdParsed, subprincipleSetInput)
        notificationService.changesSaved()
        setInitialValues(values)
        setFormDirty(false)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } catch (e) {
        notificationService.operationFailed()
      }
    },
    [
      getMessage,
      isSubprincipleAssessedInPrograms,
      notificationService,
      onBackHandler,
      parentPrincipleOptions,
      subprincipleId,
      urqlClient,
    ],
  )

  const initFormData = useCallback(async () => {
    try {
      const subprincipleToEditRes = await fetchSubprincipleConfigToEdit()
      setInitialValues(subprincipleToEditRes.editingSubprincipleConfig)

      const parentPrincipleOptionsRes = await initParentPrincipleOptions()
      setParentPrincipleOptions(parentPrincipleOptionsRes.parentPrincipleOptions)

      const isSubprincipleAssessed = subprincipleToEditRes.subprincipleTimesAssessed !== 0
      setIsSubprincipleAssessedInPrograms(isSubprincipleAssessed)
    } catch (e) {
      notificationService.operationFailed()
    }
  }, [initParentPrincipleOptions, fetchSubprincipleConfigToEdit, notificationService])

  useEffect(() => {
    initFormData()
  }, [initFormData])

  const formFieldOptions = {
    statusFieldOptions,
    parentPrincipleOptions,
  }

  return (
    <ScreenLayout
      title={getMessage('label.configuration.subprinciples.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <SubprinciplesConfigurationForm
              mode="EDIT"
              onSubmit={validateAndHandleSubmit}
              initialValues={initialValues}
              formFieldOptions={formFieldOptions}
              isSubprincipleAssessedInPrograms={isSubprincipleAssessedInPrograms}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
